import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { Phone, Menu, X, Facebook, Instagram } from 'lucide-react';
import { companyInfo } from '../mock';

const Header = ({ showBanner = true }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navLinks = [
    { name: 'HOME', href: '/#home' },
    { name: 'WHY US', href: '/#why-us' },
    { name: 'SERVICES', href: '/#services' },
    { name: 'PRICING', href: '/#pricing' },
    { name: 'GALLERY', href: '/#gallery' },
    { name: 'TESTIMONIALS', href: '/#testimonials' },
    { name: 'CONTACT', href: '/#contact' },
  ];

  return (
    <>
      {/* Special Offer Banner */}
      {showBanner && (
        <div className="bg-blue-600 text-white py-2 md:py-3 px-4 sticky top-0 z-[60]">
          <div className="max-w-7xl mx-auto text-center">
            <div className="flex flex-col md:flex-row items-center justify-center gap-2 md:gap-6">
              <div>
                <span className="text-sm md:text-xl font-bold">🌸 Spring Special — 3 Rooms for Only $125 + FREE Vacuuming + FREE Deodorization!</span>
              </div>
              <a href="/#contact" className="hidden md:block">
                <Button className="bg-white text-blue-600 hover:bg-blue-50 font-bold px-6 py-2 shadow-lg" data-testid="banner-book-now-btn">
                  Book Now
                </Button>
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <header className={`bg-white shadow-sm sticky ${showBanner ? 'top-[52px] md:top-[48px]' : 'top-0'} z-50 border-b border-gray-100`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-1">
          {/* Mobile Header */}
          <div className="flex md:hidden justify-between items-center py-2">
            <button 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="text-cyan-600 p-2"
              aria-label="Toggle menu"
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
            <Link to="/" className="flex-1 flex justify-center">
              <img src={companyInfo.logo} alt={companyInfo.name} className="h-10" />
            </Link>
            <a 
              href="tel:8255837478"
              className="bg-cyan-600 text-white px-3 py-2 rounded-lg font-bold text-sm flex items-center gap-1"
              data-testid="mobile-call-btn"
            >
              <Phone className="h-4 w-4" />
              Call Now
            </a>
          </div>

          {/* Mobile Menu Dropdown */}
          {mobileMenuOpen && (
            <div className="md:hidden bg-cyan-600 rounded-lg mt-2 py-4 px-4">
              <nav className="flex flex-col space-y-4">
                {navLinks.map((link) => (
                  <a
                    key={link.href}
                    href={link.href}
                    onClick={() => setMobileMenuOpen(false)}
                    className="text-white hover:text-blue-100 transition-colors font-medium"
                  >
                    {link.name}
                  </a>
                ))}
                <a 
                  href={`tel:${companyInfo.phone.replace(/[^0-9]/g, '')}`} 
                  className="flex items-center text-white hover:text-blue-100 transition-colors font-medium"
                >
                  <Phone className="h-4 w-4 mr-2" />
                  {companyInfo.phone}
                </a>
              </nav>
            </div>
          )}

          {/* Desktop Header */}
          <div className="hidden md:flex flex-col">
            <div className="flex justify-between items-center">
              <Link to="/" className="flex items-center space-x-2">
                <img src={companyInfo.logo} alt={companyInfo.name} className="h-16 md:h-20" />
              </Link>
              <nav className="flex items-center space-x-6">
                {navLinks.map((link) => (
                  <a
                    key={link.href}
                    href={link.href}
                    className="transition-colors font-medium text-cyan-600 hover:text-cyan-700"
                    data-testid={`nav-${link.name.toLowerCase().replace(' ', '-')}`}
                  >
                    {link.name}
                  </a>
                ))}
                <Button 
                  className="bg-cyan-600 hover:bg-cyan-700 text-white text-base px-5 py-3"
                  asChild
                >
                  <a href={`tel:${companyInfo.phone.replace(/[^0-9]/g, '')}`}>
                    <Phone className="h-4 w-4 mr-2" />
                    {companyInfo.phone}
                  </a>
                </Button>
              </nav>
            </div>
            <div className="flex justify-end items-center gap-4 mt-2">
              <a 
                href="https://www.facebook.com/share/17iS7zmp9J/?mibextid=wwXIfr" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-cyan-600 hover:text-cyan-700 transition-colors"
                aria-label="Facebook"
              >
                <Facebook className="h-7 w-7" />
              </a>
              <a 
                href="https://www.instagram.com/carpexcleaning?igsh=ZTUxYW90b3h3Z2Y4&utm_source=ig_contact_invite" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-cyan-600 hover:text-cyan-700 transition-colors"
                aria-label="Instagram"
              >
                <Instagram className="h-7 w-7" />
              </a>
              <a 
                href="https://www.yelp.ca/biz/carpex-cleaning-services-calgary" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-cyan-600 hover:text-cyan-700 transition-colors"
                aria-label="Yelp"
              >
                <svg className="h-7 w-7" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.16 12.594l-4.995 1.433c-.96.276-1.74-.8-1.176-1.63l2.905-4.308a1.072 1.072 0 0 1 1.596-.206 9.194 9.194 0 0 1 2.364 3.252 1.073 1.073 0 0 1-.694 1.459zm-3.397 5.906a1.073 1.073 0 0 1-1.168-.631l-1.883-4.763c-.372-.94.606-1.835 1.476-1.348l4.489 2.506c.585.327.708 1.119.252 1.588a9.201 9.201 0 0 1-3.166 2.648zM9.5 12.095l2.56-4.071c.506-.806 1.69-.543 1.786.397l.498 4.876a1.073 1.073 0 0 1-.886 1.159 9.194 9.194 0 0 1-3.983-.219 1.073 1.073 0 0 1 .025-2.142zm.39 8.226a1.073 1.073 0 0 1-1.28-.793l-.906-3.466c-.253-.97.833-1.727 1.635-1.138l4.017 2.95c.54.397.495 1.203-.089 1.536a9.194 9.194 0 0 1-3.377 1.91zM5.163 5.252a9.194 9.194 0 0 1 3.6-2.53 1.073 1.073 0 0 1 1.453.691l1.132 3.888c.282.97-.746 1.797-1.553 1.247L5.28 5.34a.268.268 0 0 1-.117-.088z"/>
                </svg>
              </a>
            </div>
          </div>
        </div>
      </header>
    </>
  );
};

export default Header;
